import { Component, Inject, OnDestroy, OnInit, ViewChild, NgZone, AfterViewInit, HostListener } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';
import { GoogleMap } from '@angular/google-maps';
import { trackVehicleImages } from 'src/@order/commonData/commonUiData';
import { liveTraking, ordersTracking } from 'src/app/utils/systemEnums';

@Component({
  selector: 'app-track-driver',
  templateUrl: './track-driver.component.html',
  styleUrls: ['./track-driver.component.scss']
})
export class TrackDriverComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('map') map: GoogleMap;

  heading: string;
  order: any;
  dir: any = {
    destination: null,
    origin: null,
    bearing: 0
  };

  // Order status properties
  isOrderAccepted: boolean = false;
  isOrderPickedUp: boolean = false;
  waypoints: any[] = [];
  routePath: google.maps.LatLngLiteral[] = [];
  mapCenter: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  zoom = 12;

  // Use a small threshold for detecting significant movement
  private routeUpdateThreshold = 0.05; // 30 meters
  private lastRouteUpdatePosition: google.maps.LatLngLiteral | null = null;

  // Use subjects for smoother updates
  private locationUpdateSubject = new Subject<google.maps.LatLngLiteral>();
  private routeUpdateSubject = new Subject<void>();

  routeBuilt = false;
  driverIcon = {
    url: '',
    scaledSize: new google.maps.Size(60, 60),
    anchor: new google.maps.Point(20, 20)
  };

  private driverSubscription: Subscription;
  private dropPointsSubscription: Subscription;
  private locationUpdateSubscription: Subscription;
  private routeUpdateSubscription: Subscription;
  private lastKnownPosition: google.maps.LatLngLiteral | null = null;
  private directionsService: google.maps.DirectionsService;
  private routeRenderInProgress = false;

  private positionQueue: google.maps.LatLngLiteral[] = [];
  private isAnimating = false;


  private gradientPolyline: google.maps.Polyline;
  private animationSpeed = 500;

  private driverMarker: google.maps.marker.AdvancedMarkerElement;
  private animationFrameId: number | null = null;

  markerOptions = {
    origin: {
      icon: {
        url: '',
        anchor: new google.maps.Point(20, 20),
        rotation: 0
      }
    },
    destination: {
      icon: {
        url: 'assets/images/tracking/drop_off.png',
        scaledSize: new google.maps.Size(40, 40)
      }
    },
    pickup: {
      icon: {
        url: 'assets/images/tracking/pickup_icon.png',
        scaledSize: new google.maps.Size(40, 40)
      }
    },
    // waypoints: {
    //   icon: {
    //     url: 'assets/images/tracking/map-marker.png'
    //   }
    // }
  };

  polylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#1E88E5',
    strokeOpacity: 0.9,
    strokeWeight: 5
  };

  private driverIconContainer: HTMLDivElement | null = null;
  private driverIconElement: HTMLImageElement | null = null;
  routePolyline: google.maps.Polyline;
  pickupCoords: { lat: number; lng: number; };


  private originalRoutePath: google.maps.LatLngLiteral[] = [];
  private routeDeviationThreshold = 0.1; // 100 meters - threshold to trigger new route calculation
  private hasRouteDeviation = false;

  private apiCallCount = 0;
  private lastApiCallTime: Date | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public _data: any,
    private afs: AngularFirestore,
    public _matDialogRef: MatDialogRef<TrackDriverComponent>,
    private ngZone: NgZone
  ) {
    this.directionsService = new google.maps.DirectionsService();
  }

  ngOnInit(): void {
    this.heading = this._data.heading;
    this.order = this._data.order;

    // Set order status flags
    const orderStatus = this.order?.deliveryStatus || this.order?.status;

    this.isOrderAccepted = orderStatus === 3;
    this.isOrderPickedUp = orderStatus === 4;

    console.log('Order Status:', orderStatus, 'isOrderAccepted:', this.isOrderAccepted, 'isOrderPickedUp:', this.isOrderPickedUp);
    console.log('Route Strategy:', !this.isOrderPickedUp ? 'To Pickup Location' : 'To Destination with Waypoints');

    // Set up the location update stream with debounce to smooth rapid updates
    this.locationUpdateSubscription = this.locationUpdateSubject
      .pipe(debounceTime(100)) // Process location updates after 100ms of stability
      .subscribe(newLocation => {
        this.processDriverLocationUpdate(newLocation);
      });

    // Set up the route update stream with throttle to prevent too many API calls
    this.routeUpdateSubscription = this.routeUpdateSubject
      .pipe(throttleTime(3000)) // At most one route update every 3 seconds
      .subscribe(() => {
        if (!this.routeRenderInProgress) {
          this.buildRouteToDestination();
        }
      });

    this.dropPointsSubscription = this.updateDropoffPoint(String(this.order.id))
      .subscribe((res: any) => {
        if (res) {
          // Set the vehicle image first
          this.setDriverVehicleImage(res.details.vehicleType);

          if (res.details.dropArray && res.details.dropArray.length > 0) {
            const sorted = res.details.dropArray.sort((a, b) => (a.id > b.id ? 1 : -1));

            // Clear waypoints array to prevent duplicates
            this.waypoints = [];
            this.clearExistingMarkers(); // Clear existing markers before adding new ones



            this.pickupCoords = {
              lat: Number(res.details.pickup.coordinates.latitude),
              lng: Number(res.details.pickup.coordinates.longitude)
            };



            sorted.forEach((point, i) => {
              const coords = {
                lat: Number(point.coordinates.latitude),
                lng: Number(point.coordinates.longitude)
              };

              if (i === sorted.length - 1) {
                this.dir.destination = coords;
              } else {
                this.waypoints.push({
                  location: coords,
                  stopover: true
                });
              }

              // Create a marker for each drop point only when order is picked up
              if (this.isOrderPickedUp) {
                console.log('Creating destination marker for drop point:', coords);
                this.createMarker(coords, 'assets/images/tracking/drop_off.png');
              } else {
                console.log('Skipping destination marker creation - order not picked up yet');
              }
            });

            // Subscribe to driver location after setting up waypoints and destination
            this.subscribeToDriverLocation();
          }
        }
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    // Update the route display and map view on resize
    if (this.routeBuilt) {
      this.updateRoutePolyline();
      this.updateInitialMapView();
    }
  }

  ngAfterViewInit(): void {
    if (this.map?.googleMap) {
      setTimeout(() => {
        const mapOptions: google.maps.MapOptions = {
          zoom: this.zoom,
          center: this.mapCenter,
          mapId: 'd6c938ddd2307338',
          gestureHandling: 'cooperative',
          zoomControl: true,
          disableDefaultUI: false,
          clickableIcons: false, // Disable POI clicks for better performance
          mapTypeControl: false  // Disable map type control for cleaner UI
        };

        const map = this.map.googleMap;
        map.setOptions(mapOptions);

        // Remove any existing marker if it exists
        if (this.driverMarker) {
          this.driverMarker.map = null;
        }

        // Create container once
        this.driverIconContainer = document.createElement('div');
        this.driverIconContainer.style.position = 'relative';
        this.driverIconContainer.style.width = '60px';
        this.driverIconContainer.style.height = '60px';

        // Create image once
        this.driverIconElement = document.createElement('img');
        this.driverIconElement.style.width = '60px';
        this.driverIconElement.style.height = '60px';
        this.driverIconElement.style.transition = 'transform 0.5s ease';
        this.driverIconElement.style.display = 'block';
        this.driverIconElement.style.position = 'absolute';
        this.driverIconElement.style.top = '0';
        this.driverIconElement.style.left = '0';
        this.driverIconElement.style.willChange = 'transform'; // Performance optimization

        // Set the initial image URL if available
        if (this.driverIcon.url) {
          this.driverIconElement.src = this.driverIcon.url;
        }

        // Set initial rotation
        const initialRotation = this.dir.bearing || 0;
        console.log("Initial rotation:", initialRotation);

        // this.driverIconElement.style.transform = `rotate(${initialRotation}deg)`;

        // Add image to container
        this.driverIconContainer.appendChild(this.driverIconElement);

        // Create marker with container
        this.driverMarker = new google.maps.marker.AdvancedMarkerElement({
          position: this.dir.origin || { lat: 0, lng: 0 },
          content: this.driverIconContainer,
          map: map,
          zIndex: 1000 // Ensure driver is on top of route
        });
      });

      google.maps.event.addListener(this.map.googleMap, 'zoom_changed', () => {
        this.ngZone.run(() => {
          if (this.routeBuilt) {
            this.updateRoutePolyline();
          }
        });
      });

    }
  }

  subscribeToDriverLocation(): void {
    if (this.driverSubscription) {
      this.driverSubscription.unsubscribe();
    }

    this.driverSubscription = this.updateTrackingDoc(String(this.order.driverDetail.id))
      .subscribe((res: any) => {
        if (res?.driverlocation) {
          try {
            const newLocation = {
              lat: Number(res.driverlocation._lat),
              lng: Number(res.driverlocation._long)
            };

            if (isNaN(newLocation.lat) || isNaN(newLocation.lng) ||
              Math.abs(newLocation.lat) > 90 || Math.abs(newLocation.lng) > 180) {
              console.error('Invalid coordinates received:', newLocation);
              return;
            }

            // Set driver vehicle image first if needed
            if (!this.driverIcon.url && res.details?.vehicleType) {
              this.setDriverVehicleImage(res.details.vehicleType);
            }

            // Push to subject for smooth handling
            this.locationUpdateSubject.next(newLocation);
          } catch (error) {
            console.error('Error processing driver location:', error);
          }
        }
      });
  }

  processDriverLocationUpdate(newLocation: google.maps.LatLngLiteral): void {
    if (!this.dir.origin) {
      // First location update
      this.dir.origin = newLocation;
      this.lastKnownPosition = newLocation;
      this.mapCenter = newLocation;
      this.lastRouteUpdatePosition = newLocation;

      if (this.driverMarker) {
        this.driverMarker.position = newLocation;
      }

      // Build initial route based on pickup status
      if ((!this.isOrderPickedUp && this.pickupCoords) || (this.isOrderPickedUp && this.dir.destination)) {
        this.buildRouteToDestination(true); // Force initial route build
      }
    } else {
      // Update driver position
      this.updateDriverPosition(newLocation);
    }

    // Ensure the marker is properly displayed on the map
    // if (this.driverMarker && !this.driverMarker.map && this.map?.googleMap) {
    //   this.driverMarker.map = this.map.googleMap;
    // }
  }



  buildRouteToDestination(forceUpdate = false): void {
    // Determine the target destination based on pickup status
    let targetDestination;
    if (!this.isOrderPickedUp && this.pickupCoords) {
      // If order is not picked up, route to pickup location
      targetDestination = this.pickupCoords;
    } else if (this.isOrderPickedUp && this.dir.destination) {
      // If order is picked up, route to destination
      targetDestination = this.dir.destination;
    } else {
      console.warn('Cannot build route: missing origin or target destination');
      return;
    }

    if (!this.dir.origin || !targetDestination) {
      console.warn('Cannot build route: missing origin or target destination');
      return;
    }

    // Log route check
    console.log(`[Route Check] Force update: ${forceUpdate}, Has deviation: ${this.hasRouteDeviation}, Original path points: ${this.originalRoutePath.length}`);

    // If we already have a route and this isn't a forced update, check for deviation
    if (this.originalRoutePath.length > 0 && !forceUpdate && !this.hasRouteDeviation) {
      const deviationDistance = this.calculateDistanceFromRoute(this.dir.origin);
      console.log(`[Deviation Check] Distance from route: ${deviationDistance.toFixed(4)} km, Threshold: ${this.routeDeviationThreshold} km`);

      if (deviationDistance < this.routeDeviationThreshold) {
        console.log('[Route Update] Driver on route - updating polyline only');
        this.updateRoutePolyline();
        return;
      } else {
        this.hasRouteDeviation = true;
        console.log('[Route Update] Significant deviation detected - recalculating route');
      }
    }

    this.routeRenderInProgress = true;
    this.apiCallCount++;
    this.lastApiCallTime = new Date();

    console.log(`[API Call #${this.apiCallCount}] Initiating Directions API request at ${this.lastApiCallTime.toISOString()}`);
    console.log(`[API Params] Origin: ${this.dir.origin.lat},${this.dir.origin.lng} | Target: ${targetDestination.lat},${targetDestination.lng} | Waypoints: ${this.waypoints.length}`);
    console.log(`[Route Type] ${!this.isOrderPickedUp ? 'To Pickup' : 'To Destination'}`);

    // Determine waypoints based on pickup status
    let routeWaypoints = [];
    if (this.isOrderPickedUp) {
      // If picked up, use all waypoints to destination
      routeWaypoints = this.waypoints;
    }
    // If not picked up, no waypoints (direct to pickup)

    this.directionsService.route({
      origin: this.dir.origin,
      destination: targetDestination,
      waypoints: routeWaypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: false,
      avoidFerries: true,
      avoidHighways: false,
      avoidTolls: false
    }, (response, status) => {
      const callEndTime = new Date();
      const duration = this.lastApiCallTime ? callEndTime.getTime() - this.lastApiCallTime.getTime() : 0;

      this.ngZone.run(() => {
        if (status === 'OK' && response.routes.length > 0) {
          console.log(`[API Success #${this.apiCallCount}] Completed in ${duration}ms with ${response.routes[0].legs.length} legs`);

          this.routePath = [];
          this.originalRoutePath = [];

          const totalPoints = response.routes[0].legs.reduce((acc, leg) => {
            return acc + leg.steps.reduce((stepAcc, step) => stepAcc + step.path.length, 0);
          }, 0);

          console.log(`[Route Details] Total path points: ${totalPoints}`);

          response.routes[0].legs.forEach(leg => {
            leg.steps.forEach(step => {
              step.path.forEach(point => {
                this.routePath.push({ lat: point.lat(), lng: point.lng() });
                this.originalRoutePath.push({ lat: point.lat(), lng: point.lng() });
              });
            });
          });

          this.updateRoutePolyline();
          this.lastRouteUpdatePosition = { ...this.dir.origin };
          this.routeBuilt = true;
          this.hasRouteDeviation = false;
        } else {
          console.error(`[API Error #${this.apiCallCount}] Status: ${status} after ${duration}ms`);
          this.createFallbackRoute();
        }

        this.routeRenderInProgress = false;
      });
    });
  }

  private updateRoutePolyline(): void {
    if (!this.map?.googleMap) return;

    // Get current zoom level
    const currentZoom = this.map.googleMap.getZoom();
    const strokeWeight = currentZoom > 15 ? 7 : (currentZoom > 12 ? 5 : 3);

    // Remove existing polylines if they exist
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
    }
    if (this.gradientPolyline) {
      this.gradientPolyline.setMap(null);
    }

    // If we have an original route path, use that as the base
    const basePath = this.originalRoutePath.length > 0 ? this.originalRoutePath : this.routePath;

    // Create new polyline with improved styling
    this.routePolyline = new google.maps.Polyline({
      path: basePath,
      geodesic: true,
      strokeColor: '#1E88E5',
      strokeOpacity: 0.9,
      strokeWeight: strokeWeight,
      map: this.map.googleMap,
      zIndex: 1
    });

    // Create gradient polyline from start to current position
    if (this.dir.origin && basePath.length > 0) {
      // Find the closest point on the route to current position
      let closestPointIndex = 0;
      let closestDistance = Number.MAX_VALUE;

      basePath.forEach((point, index) => {
        const distance = this.calculateDistance(
          this.dir.origin.lat, this.dir.origin.lng,
          point.lat, point.lng
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPointIndex = index;
        }
      });

      // Create gradient path from start to current point
      const gradientPath = basePath.slice(0, closestPointIndex + 1);

      // Only add current position if we're significantly off the route
      if (closestDistance > 0.01) { // 10 meters
        gradientPath.push(this.dir.origin);
      }

      this.gradientPolyline = new google.maps.Polyline({
        path: gradientPath,
        geodesic: true,
        strokeColor: '#4CAF50',
        strokeOpacity: 1.0,
        strokeWeight: strokeWeight,
        map: this.map.googleMap,
        zIndex: 2
      });
    }
  }

  private calculateDistanceFromRoute(point: google.maps.LatLngLiteral): number {
    if (!this.originalRoutePath || this.originalRoutePath.length === 0) {
      return Number.MAX_VALUE;
    }

    let minDistance = Number.MAX_VALUE;

    // Find the closest point on the route
    this.originalRoutePath.forEach(routePoint => {
      const distance = this.calculateDistance(
        point.lat, point.lng,
        routePoint.lat, routePoint.lng
      );
      if (distance < minDistance) {
        minDistance = distance;
      }
    });

    return minDistance;
  }


  updateDriverPosition(newLocation: google.maps.LatLngLiteral): void {
    if (!newLocation || (isNaN(newLocation.lat) || isNaN(newLocation.lng))) {
      console.error("Invalid location received:", newLocation);
      return;
    }

    // Snap to nearest road point if we have a route
    let snappedPosition = this.originalRoutePath.length > 0 ? this.snapToRoute(newLocation) : newLocation;

    this.dir.origin = snappedPosition;

    if (!this.lastKnownPosition) {
      this.lastKnownPosition = snappedPosition;
      this.mapCenter = snappedPosition;
      if (this.driverMarker) {
        this.driverMarker.position = snappedPosition;
      }
      return;
    }

    const distance = this.calculateDistance(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      snappedPosition.lat, snappedPosition.lng
    );

    if (distance < 0.0005) {
      return;
    }

    // Calculate bearing using snapped positions
    const bearing = this.calculateBearing(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      snappedPosition.lat, snappedPosition.lng
    );

    this.dir.bearing = bearing;

    // Update rotation immediately
    if (this.driverIconElement) {
      this.driverIconElement.style.transform = `rotate(${bearing}deg)`; // Ensure the icon rotates to the correct direction
    }

    this.positionQueue.push(snappedPosition);

    if (!this.isAnimating) {
      this.processNextPositionInQueue();
    }

    if (distance > this.routeUpdateThreshold) {
      this.routeUpdateSubject.next();
    }
  }


  private snapToRoute(point: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
    if (!this.originalRoutePath || this.originalRoutePath.length === 0) {
      return point;
    }

    let closestPoint = this.originalRoutePath[0];
    let closestDistance = this.calculateDistance(
      point.lat, point.lng,
      closestPoint.lat, closestPoint.lng
    );

    for (const routePoint of this.originalRoutePath) {
      const distance = this.calculateDistance(
        point.lat, point.lng,
        routePoint.lat, routePoint.lng
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = routePoint;
      }
    }

    // Only snap if reasonably close to the route
    return closestDistance < 0.05 ? closestPoint : point; // 50 meter threshold
  }



  private processNextPositionInQueue(): void {
    if (this.positionQueue.length === 0) {
      this.isAnimating = false;
      return;
    }

    this.isAnimating = true;

    // Get the next position from the queue
    const newPosition = this.positionQueue.shift();

    // Calculate bearing for vehicle rotation
    const bearing = this.calculateBearing(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      newPosition.lat, newPosition.lng
    );

    this.dir.bearing = bearing;
    if (this.markerOptions.origin.icon) {
      this.markerOptions.origin.icon.rotation = bearing;
    }


    console.log("helo in animateMarkerMovement");

    // Animate to this position
    this.animateMarkerMovement(this.lastKnownPosition, newPosition, bearing);
  }


  animateMarkerMovement(start: google.maps.LatLngLiteral, end: google.maps.LatLngLiteral, bearing: number): void {
    // Cancel any ongoing animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const startTime = performance.now();
    const duration = 1000;
    const startBearing = this.dir.bearing;
    const bearingDiff = this.getShortestRotation(startBearing, bearing);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = this.easeInOutQuintic(progress);

      // console.log("currentTime , startTime, progress  ", currentTime, startTime, progress);

      const interpolatedLat = start.lat + (end.lat - start.lat) * easeProgress;
      const interpolatedLng = start.lng + (end.lng - start.lng) * easeProgress;
      const interpolatedBearing = startBearing + bearingDiff * easeProgress;




      this.ngZone.run(() => {
        // Update marker position and rotation
        if (this.driverMarker && this.driverIconElement) {
          this.driverMarker.position = { lat: interpolatedLat, lng: interpolatedLng };

          // IMPORTANT FIX: Apply rotation correctly and consistently
          const rotationTransform = `rotate(${interpolatedBearing}deg)`;

          // // Add subtle scale effect during movement
          // const scaleFactor = 1 + Math.sin(progress * Math.PI) * 0.05; // 5% scale effect

          // // Apply both transformations together
          // this.driverIconElement.style.transform = `${rotationTransform}`;

          // Add subtle shadow effect during movement
          this.driverIconElement.style.filter = 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))';
        }

        // Handle map view adjustments if needed
        if (this.map && this.map.googleMap) {
          const bounds = this.map.googleMap.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            const buffer = 0.25; // Keep driver in center 75% of screen (was 0.3)
            const viewWidth = ne.lng() - sw.lng();
            const viewHeight = ne.lat() - sw.lat();

            const minLng = sw.lng() + viewWidth * buffer;
            const maxLng = ne.lng() - viewWidth * buffer;
            const minLat = sw.lat() + viewHeight * buffer;
            const maxLat = ne.lat() - viewHeight * buffer;

            if (interpolatedLat < minLat || interpolatedLat > maxLat ||
              interpolatedLng < minLng || interpolatedLng > maxLng) {
              // Use smooth pan effect
              this.map.googleMap.panTo({ lat: interpolatedLat, lng: interpolatedLng });
            }
          }
        }
      });

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = null;
        this.lastKnownPosition = end;

        // IMPORTANT FIX: Update bearing consistently
        this.dir.bearing = bearing;

        console.log("hello in the else part", this.dir.bearing);


        // // IMPORTANT FIX: Ensure final rotation is applied correctly
        if (this.driverIconElement) {
          // this.driverIconElement.style.transform = `rotate(${bearing}deg)`;
          this.driverIconElement.style.filter = '';
        }

        // Update the gradient polyline
        this.updateRoutePolyline();

        // Process the next position in the queue
        this.processNextPositionInQueue();
      }

    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private easeInOutQuintic(t: number): number {
    return t < 0.5
      ? 16 * t * t * t * t * t
      : 1 - Math.pow(-2 * t + 2, 5) / 2;
  }

  // Cubic easing for smoother animations
  easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Get shortest rotation path (clockwise or counterclockwise)
  getShortestRotation(startAngle: number, endAngle: number): number {
    let diff = endAngle - startAngle;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff;
  }

  createFallbackRoute(): void {
    if (!this.dir.origin || !this.dir.destination) return;

    const routePoints = [this.dir.origin];
    this.waypoints.forEach(wp => {
      routePoints.push(wp.location);
    });
    routePoints.push(this.dir.destination);

    this.routePath = routePoints;
    this.routeBuilt = true;
    this.updateRoutePolyline();
    this.updateInitialMapView();
  }

  updateInitialMapView(): void {
    if (!this.routePath || this.routePath.length === 0 || !this.map?.googleMap) {
      return;
    }

    // Create bounds that include all route points
    const bounds = new google.maps.LatLngBounds();

    // Add origin
    if (this.dir.origin) {
      bounds.extend(new google.maps.LatLng(this.dir.origin.lat, this.dir.origin.lng));
    }

    // Add target destination based on pickup status
    if (!this.isOrderPickedUp && this.pickupCoords) {
      // If not picked up, include pickup location
      bounds.extend(new google.maps.LatLng(this.pickupCoords.lat, this.pickupCoords.lng));
    } else if (this.isOrderPickedUp && this.dir.destination) {
      // If picked up, include destination and waypoints
      bounds.extend(new google.maps.LatLng(this.dir.destination.lat, this.dir.destination.lng));

      // Add waypoints only when picked up
      this.waypoints.forEach(waypoint => {
        bounds.extend(new google.maps.LatLng(waypoint.location.lat, waypoint.location.lng));
      });
    }

    // Add route path points
    this.routePath.forEach(point => {
      bounds.extend(new google.maps.LatLng(point.lat, point.lng));
    });

    // Apply bounds with padding
    this.map.googleMap.fitBounds(bounds, 50); // 50 pixels padding
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * Math.PI / 180;
  }



  calculateBearing(startLat: number, startLng: number, destLat: number, destLng: number): number {
    const toRadians = (deg: number) => deg * Math.PI / 180;
    const toDegrees = (rad: number) => rad * 180 / Math.PI;

    const φ1 = toRadians(startLat);
    const φ2 = toRadians(destLat);
    const λ1 = toRadians(startLng);
    const λ2 = toRadians(destLng);

    const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) -
      Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
    const θ = Math.atan2(y, x);

    return (toDegrees(θ) + 360) % 360; // normalize to 0-360
  }

  setDriverVehicleImage(type: number): void {
    const vehicle = trackVehicleImages.filter((item) => item.id == type)[0];
    if (vehicle) {
      this.markerOptions.origin.icon.url = vehicle.icon;
      this.driverIcon.url = vehicle.icon;

      // Update the existing image src if it exists
      if (this.driverIconElement) {
        this.driverIconElement.src = vehicle.icon;
      } else {
        this.markerOptions.origin.icon.url = 'assets/images/tracking/bike_pin.png'
        this.driverIconElement.src = 'assets/images/tracking/bike_pin.png'
      }
    } else {
      // Default image
      const defaultIcon = 'assets/images/tracking/bike_pin.png';
      this.markerOptions.origin.icon.url = defaultIcon;
      this.driverIcon.url = defaultIcon;

      if (this.driverIconElement) {
        this.driverIconElement.src = defaultIcon;
      }
    }
  }

  updateTrackingDoc(id: string) {
    return this.afs.collection(liveTraking).doc(id).valueChanges();
  }

  updateDropoffPoint(id: string) {
    return this.afs.collection(ordersTracking).doc(id).valueChanges();
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear the position queue
    this.positionQueue = [];
    this.isAnimating = false;

    // Clear all created markers
    this.clearExistingMarkers();

    if (this.driverSubscription) {
      this.driverSubscription.unsubscribe();
    }

    if (this.dropPointsSubscription) {
      this.dropPointsSubscription.unsubscribe();
    }

    if (this.locationUpdateSubscription) {
      this.locationUpdateSubscription.unsubscribe();
    }

    if (this.routeUpdateSubscription) {
      this.routeUpdateSubscription.unsubscribe();
    }


    console.log(`[API Summary] Total API calls during component life: ${this.apiCallCount}`);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  // New method to create markers for drop points
  createMarker(position: google.maps.LatLngLiteral, iconUrl: string): void {
    const marker = new google.maps.Marker({
      position: position,
      map: this.map.googleMap,
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40) // Adjust size as needed
      }
    });

    // Track the marker for cleanup
    this.createdMarkers.push(marker);
  }

  // Array to track created markers for cleanup
  private createdMarkers: google.maps.Marker[] = [];

  // New method to clear existing markers (if needed)
  clearExistingMarkers(): void {
    // Clear all programmatically created markers
    this.createdMarkers.forEach(marker => {
      marker.setMap(null);
    });
    this.createdMarkers = [];
  }
}